// features/subscription/CreditSuccess.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { FaCheckCircle, FaSpinner } from 'react-icons/fa';

const CreditSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshCreditBalance } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError(t('credits.noSessionId'));
      setLoading(false);
      return;
    }

    // Only verify if we haven't already attempted verification
    if (!verificationAttempted.current) {
      const verifySession = async () => {
        try {
          // Mark verification as attempted immediately to prevent concurrent calls
          verificationAttempted.current = true;
          setLoading(true);
          console.log('Verifying credit purchase session:', sessionId);
          
          const result = await api.verifyCreditPurchaseCheckout(sessionId);
          setVerificationResult(result);
          
          // Refresh credit balance in context
          await refreshCreditBalance();
        } catch (err) {
          console.error('Error verifying credit purchase:', err);
          setError(err.message || t('credits.verificationError'));
        } finally {
          setLoading(false);
        }
      };

      verifySession();
    }
  }, [searchParams, refreshCreditBalance, t]);

  const handleNavigateToDashboard = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <Card className="w-full max-w-md mx-auto mt-10">
        <CardHeader className="text-center">
          <CardTitle>{t('credits.processingPayment')}</CardTitle>
          <CardDescription>{t('credits.pleaseWait')}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <FaSpinner className="animate-spin text-primary text-4xl" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto mt-10">
        <CardHeader className="text-center">
          <CardTitle className="text-red-600">{t('credits.paymentError')}</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-center">
          <Button onClick={handleNavigateToDashboard}>
            {t('credits.returnToDashboard')}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto mt-10">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <FaCheckCircle className="text-green-500 text-5xl" />
        </div>
        <CardTitle>{t('credits.paymentSuccessful')}</CardTitle>
        <CardDescription>
          {t('credits.purchaseComplete')}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        <p>{t('credits.thankYou')}</p>
        <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-md">
          <p className="font-semibold">{t('credits.currentBalance')}</p>
          <p className="text-2xl font-bold">{verificationResult?.creditBalance || 0}</p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button onClick={handleNavigateToDashboard}>
          {t('credits.viewCredits')}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CreditSuccess;
